import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChatMessage } from '../components/ChatBox/Messages';
import { createApiUrl } from '../config/api';
import type { TabItem } from '../types/tabs';

interface SessionCache {
  sessionId: string;
  userID: string;
  messages: ChatMessage[];
  tabs?: TabItem[];
  cachedAt: Date;
  isDirty: boolean; // Track if changes need to be saved to backend
}

interface SessionStore {
  sessionCaches: Record<string, SessionCache>;
  isSaving: boolean;
  
  // Cache current session
  cacheSession: (sessionId: string, userID: string, messages: ChatMessage[], tabs: TabItem[]) => void;
  
  // Get cached session
  getSessionCache: (sessionId: string) => SessionCache | null;
  
  // Save to backend (requires auth headers)
  saveToBackend: (sessionId: string, authHeaders: Record<string, string>) => Promise<boolean>;
  
  // Load from backend (requires auth headers)
  loadFromBackend: (sessionId: string, authHeaders: Record<string, string>) => Promise<SessionCache | null>;
  
  // Mark as clean (saved)
  markClean: (sessionId: string) => void;
  
  // Clear cache
  clearSessionCache: (sessionId: string) => void;
  
  // Check if session has cache
  hasSessionCache: (sessionId: string) => boolean;
  
  // Check if session has unsaved changes
  isDirty: (sessionId: string) => boolean;
  
  // Clear all caches
  clearAllCaches: () => void;
}

export const useSessionStore = create<SessionStore>()(
  persist(
    (set, get) => ({
      sessionCaches: {},
      isSaving: false, // Always start with false - never persisted
      
      cacheSession: (sessionId, userID, messages, tabs) => {
        set((state) => {
          // Get existing cache or create new one
          const existingCache = state.sessionCaches[sessionId];
          
          // Deduplicate messages by ID, keeping the most recent version
          const messageMap = new Map();
          
          // Add existing messages first (if any)
          if (existingCache?.messages) {
            existingCache.messages.forEach(msg => {
              if (msg.id) {
                messageMap.set(msg.id, msg);
              }
            });
          }
          
          // Add/update with new messages
          messages.forEach(msg => {
            const processedMsg = {
              ...msg,
              createdAt: msg.createdAt || new Date(),
              // Simplify file attachments for serialization
              attachment: msg.attachment?.map(file => ({
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified
              })) as any
            };
            
            if (processedMsg.id) {
              messageMap.set(processedMsg.id, processedMsg);
            }
          });
          
          // Convert map back to array
          const uniqueMessages = Array.from(messageMap.values());
          
          return {
            sessionCaches: {
              ...state.sessionCaches,
              [sessionId]: {
                sessionId,
                userID,
                messages: uniqueMessages,
                tabs,
                cachedAt: new Date(),
                isDirty: true // Mark as needing save
              }
            }
          };
        });
      },
      
      getSessionCache: (sessionId) => {
        const cache = get().sessionCaches[sessionId];
        if (!cache) return null;
        
        return {
          ...cache,
          cachedAt: new Date(cache.cachedAt),
          messages: cache.messages.map(msg => ({
            ...msg,
            createdAt: msg.createdAt ? new Date(msg.createdAt) : new Date()
          }))
        };
      },
      
      saveToBackend: async (sessionId, authHeaders) => {
        const cache = get().sessionCaches[sessionId];
        if (!cache || !cache.isDirty) {
          return true;
        }
        
        set({ isSaving: true });
        
        try {
          // Prepare session data for backend
          const sessionData = {
            session_id: sessionId,
            user_id: cache.userID,
            messages: cache.messages.map(msg => ({
              id: msg.id,
              content: msg.content,
              role: msg.role,
              timestamp: msg.createdAt || new Date(),
              attachment: msg.attachment || null
            })),
            tabs_data: cache.tabs || []
          };
          const response = await fetch(createApiUrl(`/session/save_session_frontend_cache`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...authHeaders, // 🔒 Include authentication headers
            },
            body: JSON.stringify(sessionData)
          });
          if (response.ok) {
            get().markClean(sessionId);
            return true;
          } else {
            console.error('Failed to save session to backend');
            return false;
          }
        } catch (error) {
          console.error('Error saving session to backend:', error);
          return false;
        } finally {
          set({ isSaving: false });
        }
      },
      
      loadFromBackend: async (sessionId, authHeaders) => {
        try {
          const response = await fetch(createApiUrl(`/session/resume/${sessionId}`), {
            headers: authHeaders, // 🔒 Include authentication headers
          });
          
          if (response.ok) {
            const sessionData = await response.json();
            
            // Extract cache data from frontend_cache field
            const frontendCache = sessionData.frontend_cache;
            
            // Only return null if no frontend_cache exists at all
            if (!frontendCache) {
              return null;
            }
            
            // Convert backend data to cache format (tabs can be empty)
            const cache: SessionCache = {
              sessionId,
              userID: frontendCache.user_id,
              messages: (frontendCache.messages || []).map((msg: any) => ({
                id: msg.id,
                content: msg.content,
                role: msg.role,
                createdAt: msg.timestamp ? new Date(msg.timestamp) : new Date(),
                attachment: msg.attachment || undefined
              })),
              tabs: frontendCache.tabs_data || [],
              cachedAt: new Date(),
              isDirty: false
            };
            
            // Store in local cache
            set((state) => ({
              sessionCaches: {
                ...state.sessionCaches,
                [sessionId]: cache
              }
            }));
            
            return cache;
          } else {
            console.error('Failed to load session from backend:', response.statusText);
            return null;
          }
        } catch (error) {
          console.error('Error loading session from backend:', error);
          return null;
        }
      },
      
      markClean: (sessionId) => {
        set((state) => ({
          sessionCaches: {
            ...state.sessionCaches,
            [sessionId]: state.sessionCaches[sessionId] ? {
              ...state.sessionCaches[sessionId],
              isDirty: false
            } : state.sessionCaches[sessionId]
          }
        }));
      },
      
      clearSessionCache: (sessionId) => {
        set((state) => {
          const { [sessionId]: removed, ...remaining } = state.sessionCaches;
          return { sessionCaches: remaining };
        });
      },
      
      hasSessionCache: (sessionId) => {
        return !!get().sessionCaches[sessionId];
      },
      
      isDirty: (sessionId) => {
        const cache = get().sessionCaches[sessionId];
        return cache?.isDirty || false;
      },
      
      clearAllCaches: () => {
        set({ sessionCaches: {} });
      }
    }),
    {
      name: 'session-cache-storage', // localStorage key for offline backup
      partialize: (state) => ({ sessionCaches: state.sessionCaches }), // Only persist sessionCaches, NOT isSaving
      onRehydrateStorage: () => (state) => {
        // Ensure isSaving is always false after rehydration
        if (state) {
          state.isSaving = false;
        }
      }
    }
  )
);

// Enhanced hook for session-specific operations with backend integration
export const useSessionCache = (sessionId: string | null) => {
  const {
    cacheSession,
    getSessionCache,
    saveToBackend,
    loadFromBackend,
    clearSessionCache,
    hasSessionCache,
    isDirty,
    isSaving
  } = useSessionStore();
  
  const cache = sessionId ? getSessionCache(sessionId) : null;
  const hasCache = sessionId ? hasSessionCache(sessionId) : false;
  const isSessionDirty = sessionId ? isDirty(sessionId) : false;
  
  return {
    cache,
    hasCache,
    isSessionDirty,
    isSaving,
    cacheSession: sessionId ? (userID: string, messages: ChatMessage[], tabs: TabItem[]) => 
      cacheSession(sessionId, userID, messages, tabs) : () => {},
    saveToBackend: sessionId ? (authHeaders: Record<string, string>) => saveToBackend(sessionId, authHeaders) : () => Promise.resolve(false),
    loadFromBackend: sessionId ? (authHeaders: Record<string, string>) => loadFromBackend(sessionId, authHeaders) : () => Promise.resolve(null),
    clearCache: sessionId ? () => clearSessionCache(sessionId) : () => {}
  };
};